/**
 * Auto-adds protection / frozen charge product to cart.
 * - Add: bundles charge into the same /cart/add request (no drawer interference)
 * - Remove: removes charge when no qualifying products remain, patches cart/change response
 */
class FrozenCharge {
  constructor() {
    this.config = window.frozenChargeConfig;
    if (!this.config?.enabled || !this.config.variantId) return;

    this.nativeFetch = window.__shopifyNativeFetch || window.fetch.bind(window);
    this._postUpdateTimer = null;
    this.init();
  }

  init() {
    this.stampRefrigeratedPropertyOnSubmit();
    this.hookFetch();
    document.addEventListener('cartQuantityUpdated', () => this.schedulePostUpdateSync());

    window.syncFrozenCharge = () => this.syncAndRefresh(true);
  }

  isChargeVariant(variantId) {
    return String(variantId) === String(this.config.variantId);
  }

  isChargeHandle(handle) {
    return handle && this.config.chargeHandle && handle === this.config.chargeHandle;
  }

  stampRefrigeratedPropertyOnSubmit() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form?.matches?.('form[data-type="add-to-cart-form"]')) return;

        const productForm = form.closest('product-form');
        const isRefrigerated =
          productForm?.dataset.refrigerated === 'true' ||
          form.querySelector('input[name="properties[_refrigerated]"]');

        if (!isRefrigerated) return;

        if (!form.querySelector('input[name="properties[_refrigerated]"]')) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[_refrigerated]';
          input.value = 'true';
          form.appendChild(input);
        }
      },
      true
    );
  }

  schedulePostUpdateSync() {
    if (this._postUpdateTimer) clearTimeout(this._postUpdateTimer);
    this._postUpdateTimer = setTimeout(() => this.postUpdateSync(), 120);
  }

  hookFetch() {
    const self = this;
    const previousFetch = window.fetch;

    window.fetch = function (url, options = {}) {
      const requestUrl = typeof url === 'string' ? url : url?.url || '';
      const method = (options.method || 'GET').toUpperCase();
      const isCartAdd = requestUrl.includes('/cart/add') && method === 'POST';
      const isCartChange =
        (requestUrl.includes('/cart/change') || requestUrl.includes('/cart/update')) && method === 'POST';

      if (options?._frozenChargeInternal) {
        return previousFetch.apply(this, arguments);
      }

      if (isCartAdd) {
        return self
          .prepareAddRequest(options)
          .then((nextOptions) => previousFetch.call(this, url, nextOptions));
      }

      if (isCartChange) {
        return previousFetch
          .call(this, url, options)
          .then((response) => self.handleCartChangeResponse(response));
      }

      return previousFetch.apply(this, arguments);
    };
  }

  async handleCartChangeResponse(response) {
    if (!response.ok) return response;

    let data;
    try {
      data = JSON.parse(await response.text());
    } catch (error) {
      return response;
    }

    let cart = await this.getCart();
    let sections = { ...(data.sections || {}) };

    const chargeLine = this.getChargeLine(cart);
    if (chargeLine && !this.cartNeedsRefrigeratedCharge(cart)) {
      const removeResult = await this.removeChargeByLine(chargeLine);
      if (removeResult?.sections) {
        sections = { ...sections, ...removeResult.sections };
        cart = {
          item_count: removeResult.item_count ?? cart.item_count,
          items: removeResult.items ?? cart.items,
        };
      } else {
        cart = await this.getCart();
        const freshSections = await this.fetchSections(this.getSectionsToRender());
        if (freshSections) sections = { ...sections, ...freshSections };
      }
    }

    const patched = {
      ...data,
      item_count: cart.item_count,
      items: cart.items,
      sections,
    };

    return new Response(JSON.stringify(patched), {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async postUpdateSync() {
    try {
      const cart = await this.getCart();
      const chargeLine = this.getChargeLine(cart);

      if (!chargeLine || this.cartNeedsRefrigeratedCharge(cart)) return;

      const removeResult = await this.removeChargeByLine(chargeLine);
      if (!removeResult?.sections) return;

      const updatedCart = await this.getCart();
      this.applySectionsToCart(removeResult.sections, updatedCart);
    } catch (error) {
      console.warn('Frozen charge post-update sync failed:', error);
    }
  }

  async prepareAddRequest(options) {
    try {
      const cart = await this.getCart();
      if (this.getChargeLine(cart)) return options;
      if (!(await this.shouldAugmentAdd(options, cart))) return options;
      return this.augmentAddOptions(options);
    } catch (error) {
      console.warn('Frozen charge prepareAddRequest failed:', error);
      return options;
    }
  }

  async shouldAugmentAdd(options, cart) {
    if (this.config.triggerMode === 'always') {
      return !this.isChargeOnlyRequest(options);
    }

    if (this.requestHasRefrigeratedMarker(options)) return true;
    return this.cartNeedsRefrigeratedCharge(cart);
  }

  isChargeOnlyRequest(options) {
    const chargeId = String(this.config.variantId);
    const body = options?.body;

    if (body instanceof FormData) {
      if (body.has('id') && String(body.get('id')) === chargeId) return true;
    }

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        const items = json.items || [];
        return items.length === 1 && String(items[0].id) === chargeId;
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  requestHasRefrigeratedMarker(options) {
    const body = options?.body;
    if (!(body instanceof FormData)) {
      if (typeof body === 'string') {
        try {
          const json = JSON.parse(body);
          return (json.items || []).some((item) => item.properties?._refrigerated === 'true');
        } catch (error) {
          return false;
        }
      }
      return false;
    }

    for (const [key, value] of body.entries()) {
      if (key.includes('properties') && key.includes('_refrigerated') && value === 'true') {
        return true;
      }
    }

    return false;
  }

  augmentAddOptions(options) {
    const chargeId = String(this.config.variantId);
    const body = options.body;

    if (body instanceof FormData) {
      const newBody = new FormData();
      const entries = [...body.entries()];

      for (const [key, value] of entries) {
        if (key === 'sections' || key === 'sections_url') {
          newBody.append(key, value);
        }
      }

      if (body.has('id')) {
        newBody.append('items[0][id]', body.get('id'));
        newBody.append('items[0][quantity]', body.get('quantity') || '1');

        for (const [key, value] of entries) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch) {
            newBody.append(`items[0][properties][${propertyMatch[1]}]`, value);
          }
          if (key === 'selling_plan') {
            newBody.append('items[0][selling_plan]', value);
          }
        }
      } else {
        for (const [key, value] of entries) {
          if (key.startsWith('items[')) {
            newBody.append(key, value);
          }
        }
      }

      let maxIndex = -1;
      for (const [key] of newBody.entries()) {
        const match = key.match(/^items\[(\d+)\]/);
        if (match) maxIndex = Math.max(maxIndex, parseInt(match[1], 10));
      }

      const chargeIndex = maxIndex + 1;
      newBody.append(`items[${chargeIndex}][id]`, chargeId);
      newBody.append(`items[${chargeIndex}][quantity]`, '1');
      newBody.append(`items[${chargeIndex}][properties][_frozen_charge]`, 'true');

      return { ...options, body: newBody };
    }

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        if (Array.isArray(json.items)) {
          const alreadyAdded = json.items.some((item) => String(item.id) === chargeId);
          if (!alreadyAdded) {
            json.items.push({
              id: parseInt(chargeId, 10),
              quantity: 1,
              properties: { _frozen_charge: 'true' },
            });
          }
          return { ...options, body: JSON.stringify(json) };
        }
      } catch (error) {
        return options;
      }
    }

    return options;
  }

  async syncAndRefresh(force = false) {
    const changed = await this.runSync();
    if (changed || force) {
      const cart = await this.getCart();
      const sections = await this.fetchSections(this.getSectionsToRender());
      if (sections) this.applySectionsToCart(sections, cart);
    }
    return changed || force;
  }

  async runSync() {
    try {
      const cart = await this.getCart();
      const needsCharge = this.cartNeedsRefrigeratedCharge(cart);
      const chargeLine = this.getChargeLine(cart);

      if (needsCharge && !chargeLine) {
        return await this.addCharge();
      }

      if (!needsCharge && chargeLine) {
        const removeResult = await this.removeChargeByLine(chargeLine);
        if (removeResult?.sections) {
          const updatedCart = await this.getCart();
          this.applySectionsToCart(removeResult.sections, updatedCart);
          return true;
        }
      }
    } catch (error) {
      console.warn('Frozen charge sync failed:', error);
    }

    return false;
  }

  getDrawerSectionTargets() {
    return [
      { id: 'CartDrawer-CartItems', section: 'cart-drawer', selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  getSectionsToRender() {
    const sections = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    if (document.getElementById('main-cart-items')) {
      sections.push('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      sections.push(footer?.dataset?.id || 'main-cart-footer');
    }
    return [...new Set(sections)];
  }

  async fetchSections(sectionIds) {
    const ids = sectionIds?.length ? sectionIds : this.getSectionsToRender();
    if (!ids.length || !window.routes?.cart_url) return null;

    const url = `${window.routes.cart_url}?sections=${ids.join(',')}&sections_url=${encodeURIComponent(window.location.pathname)}`;
    const data = await this.nativeFetch(url).then((response) => response.json());
    return data.sections || null;
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    return target ? target.innerHTML : null;
  }

  applySectionsToCart(sections, cart) {
    if (!sections) return;

    const isEmpty = cart.item_count === 0;

    document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);

    this.getDrawerSectionTargets().forEach(({ id, section, selector }) => {
      if (!sections[section]) return;
      const root = document.getElementById(id);
      if (!root) return;
      const target = root.querySelector(selector) || root;
      const inner = this.getSectionInnerHTML(sections[section], selector);
      if (inner !== null) target.innerHTML = inner;
    });

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && sections['main-cart-items']) {
      const target = mainCartItems.querySelector('.js-contents') || mainCartItems;
      const inner = this.getSectionInnerHTML(sections['main-cart-items'], '.js-contents');
      if (inner !== null) target.innerHTML = inner;
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      const footerSectionKey = mainCartFooter.dataset?.id || 'main-cart-footer';
      if (sections[footerSectionKey]) {
        const target = mainCartFooter.querySelector('.js-contents') || mainCartFooter;
        const inner = this.getSectionInnerHTML(sections[footerSectionKey], '.js-contents');
        if (inner !== null) target.innerHTML = inner;
      }
    }
  }

  async getCart() {
    return this.nativeFetch('/cart.js').then((response) => response.json());
  }

  cartNeedsRefrigeratedCharge(cart) {
    if (this.config.triggerMode === 'always') {
      return cart.items.some((item) => !this.isChargeVariant(item.variant_id));
    }

    return cart.items.some((item) => {
      if (this.isChargeVariant(item.variant_id)) return false;
      return item.properties?._refrigerated === 'true';
    });
  }

  getChargeLine(cart) {
    return cart.items.find((item) => this.isChargeVariant(item.variant_id));
  }

  async addCharge() {
    const sections = this.getSectionsToRender();
    const response = await this.nativeFetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: this.config.variantId,
            quantity: 1,
            properties: { _frozen_charge: 'true' },
          },
        ],
        sections,
        sections_url: window.location.pathname,
      }),
      _frozenChargeInternal: true,
    });

    return response.ok;
  }

  async removeChargeByLine(chargeLine) {
    const cart = await this.getCart();
    const lineIndex = cart.items.findIndex((item) => this.isChargeVariant(item.variant_id));
    if (lineIndex < 0) return null;

    const sections = this.getSectionsToRender();
    const basePayload = {
      quantity: 0,
      sections,
      sections_url: window.location.pathname,
    };

    const request = (body) =>
      this.nativeFetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
        _frozenChargeInternal: true,
      });

    let response = await request({ ...basePayload, line: lineIndex + 1 });

    if (!response.ok && chargeLine?.key) {
      response = await request({ ...basePayload, id: chargeLine.key });
    }

    if (!response.ok) return null;

    const data = await response.json();
    return {
      sections: data.sections || null,
      item_count: data.item_count,
      items: data.items,
    };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FrozenCharge();
  });
} else {
  new FrozenCharge();
}
